import axios from "axios";

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 10000,
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const url = error.config.url;
      if (!url.includes("/login")) {
        if (url.includes("/admin")) {
          localStorage.removeItem("admin_token");
          window.location.href = "/login-admin.html?message=expired";
        } else if (url.includes("/client")) {
          localStorage.removeItem("account_customer_token");
          window.location.href = "/login-customer.html?message=expired";
        }
      }
    }
    return Promise.reject(error);
  },
);
apiClient.interceptors.request.use((config) => {
  if (config.url.startsWith("/admin")) {
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      const token = adminToken.replace(/(^"|"$)/g, "");
      config.headers.Authorization = `Bearer ${token}`;
    }
  } else if (config.url.startsWith("/client")) {
    const customerAuthToken = localStorage.getItem("account_customer_token");
    if (customerAuthToken) {
      const token = customerAuthToken.replace(/(^"|"$)/g, "");
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export const adminApi = {
  login(credentials) {
    return apiClient.post("/admin/login", credentials);
  },
  logout() {
    return apiClient.post("/admin/logout");
  },
  sendOtp(email){
      return apiClient.post("/admin/send-otp-auth", email);
  },
  verifyOTP(data){
    return apiClient.post('/admin/verifyOTP-auth', data);
  },
  loginWithOTP(credentials){
    return apiClient.post('/admin/login-with-otp', credentials);
  },
  getMe() {
    return apiClient.get("/admin/me");
  },

  getUsers(params = {}) {
    return apiClient.get("/admin/users", { params });
  },
  getUserStatistics() {
    return apiClient.get("/admin/users/statistics");
  },
  /**
   * create new user
   * @param {FormData} data - User data with avatar
   */
  createUser(data) {
    return apiClient.post("/admin/users", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /**
   * update user
   * @param {number} id - User ID
   * @param {Object | FormData} data - User data
   */
  updateUser(id, data) {
    return apiClient.post(`/admin/users/${id}`, data, {
      headers:
        data instanceof FormData
          ? { "Content-Type": "multipart/form-data" }
          : {},
    });
  },
  /**
   * assign permission user
   * @param {number} id - User ID
   */
  assignPermissionUser(id, permissions) {
    return apiClient.post(`/admin/users/${id}/permissions`, permissions);
  },

  changeUserStatus(id, status) {
    return apiClient.patch(`/admin/users/${id}/status`, { status });
  },
  deleteUser(id) {
    return apiClient.delete(`/admin/users/${id}`);
  },
  getTrashedUsers() {
    return apiClient.get("/admin/users/trashed");
  },
  /**
   * restore user from trash
   * @param {number} id - User ID
   */
  restoreUser(id) {
    return apiClient.post(`/admin/users/${id}/restore`);
  },

  getPermissions(params = {}) {
    return apiClient.get("/admin/permissions", { params });
  },

  // Customer APIs in Admin
  getCustomers(params = {}) {
    return apiClient.get("/admin/customers", { params });
  },
  createCustomer(data) {
    return apiClient.post("/admin/customers/create-customer", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getInvoicesWithCustomer(id) {
    return apiClient.get(`/admin/customers/${id}/invoices`);
  },
  search(params = {}) {
    return apiClient.get("/admin/customers/search", { params });
  },
  checkExistsPhone(params = {}) {
    return apiClient.get("/admin/customers/check-phone", { params });
  },

  //Product APIs in Admin
  createProduct(data) {
    return apiClient.post("/admin/products/create-product", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  //Invoice APIs in Admin
  createInvoice(data) {
    return apiClient.post("/admin/invoices/create-order", data);
  },
};

export const exportReceiptApi = {
  getExportReceipts(params = {}) {
    return apiClient.get("/admin/export-receipts", { params });
  },
  getTrashedExportReceipts(){
    return apiClient.get("/admin/export-receipts/trash");
  },
  getCancelledExportReceipts(){
    return apiClient.get("/admin/export-receipts/cancelled");
  },
  createExportReceipt(data) {
    return apiClient.post("/admin/export-receipts/create", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updateExportReceipt(id, data) {
    return apiClient.post(`/admin/export-receipts/update/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  softDeleteExportReceipt(id){
    return apiClient.delete(`/admin/export-receipts/soft-delete/${id}`);
  },
  cancelExportReceipt(id){
    return apiClient.post(`/admin/export-receipts/cancel/${id}`);
  },
  restoreExportReceipt(id){
    return apiClient.post(`/admin/export-receipts/restore/${id}`);
  },
  restoreStatus(id){
    return apiClient.post(`/admin/export-receipts/restore-status/${id}`);
  },
};

export const importApi = {
  importCustomers(file) {
    return apiClient.post("/import/customers", file, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  importProducts(file) {
    return apiClient.post("/import/products", file, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  importInvoices(file) {
    return apiClient.post("/import/invoices", file, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// export const exportApi = {
//   exportProducts()
// }

export const customerApi = {
  /**
   * login with phone and otp
   * @param {Object} credentials - { phone: string, otp: string }
   * @returns {Promise} - Axios response promise
   */
  login(credentials) {
    return apiClient.post("/client/login", credentials);
  },
  loginWithOtp(credentials) {
    return apiClient.post("/client/login-otp", credentials);
  },
  logout() {
    return apiClient.post("/client/logout");
  },
  sendOtp(data) {
    return apiClient.post("/client/send-otp", data);
  },
  me() {
    return apiClient.get("/client/me");
  },

  getInvoices() {
    return apiClient.get("/client/customers/get-invoices");
  },
};

export const productApi = {
  getProducts(params = {}) {
    return apiClient.get("/admin/products", { params });
  },
  getProductInTechnicalStock(params = {}) {
    return apiClient.get("/admin/products/product-technical-stock", { params });
  },
  getProductComponents(params = {}) {
    return apiClient.get("/admin/products/product-components", { params });
  },
  searchProductStillInStock(params = {}) {
    return apiClient.get("/admin/products/search", { params });
  },
  moveToShelf(id) {
    return apiClient.post(`/admin/products/${id}/move-to-shelf`);
  },
  updateProduct(id, data) {
    return apiClient.post(`/admin/products/${id}/update`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  softDeleteProduct(id){
    return apiClient.delete(`/admin/products/${id}/soft-delete`);
  },
  forceDeleteProduct(id){
    return apiClient.delete(`/admin/products/${id}/force-delete`);
  },
  getTrashProduct(){
    return apiClient.get('/admin/products/trash');
  },
  restoreProduct(id){
    return apiClient.post(`/admin/products/${id}/restore`);
  }
};

export const supplierApi = {
  getSuppliers(params = {}) {
    return apiClient.get("/admin/suppliers", { params });
  },
  createNewSupplier(data) {
    return apiClient.post("/admin/suppliers/create-supplier", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

export const invoiceApi = {
  getInvoices(params = {}) {
    return apiClient.get("/admin/invoices", { params });
  },
};

export const statisticsApi = {
  getDailyStatistics() {
    return apiClient.get("/admin/statistics/daily");
  },
  getWeeklyStatistics() {
    return apiClient.get("/admin/statistics/weekly");
  },
  getMonthlyStatistics(){
    return apiClient.get("/admin/statistics/monthly");
  },
  getThirtyDaysStatistics() {
    return apiClient.get("/admin/statistics/thirty-days");
  },
  getCustomStatistics(params = {}) {
    return apiClient.get("/admin/statistics/custom", { params });
  },
};
export default apiClient;
